<template>
  <div>
    <h1>{{ $t("components.advance.panelTitle") }}</h1>
    <delegation-menu />
    <v-data-table :headers="headers" :items="delegationAdvances" :search="search" :loading="$store.getters['Ajax/loading'] ? 'accent' : false">
      <template #header.delegation_number="{header}">
        <icon-data-table-header :header="header" />
      </template>
      <template #header.delegation_order_number="{header}">
        <icon-data-table-header :header="header" />
      </template>
      <template #header.status="{header}">
        <icon-data-table-header :header="header" />
      </template>
      <template #header.payment_type="{header}">
        <icon-data-table-header :header="header" />
      </template>
      <template #item="props">
        <tr>
          <td>
            {{ props.item.user_full_name }}
          </td>
          <td>
            {{ props.item.amount_in_currency }} {{ props.item.currency }} <span v-if="props.item.currency !== 'PLN'">({{ props.item.amount }} PLN)</span>
          </td>
          <td>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <v-icon size="24">{{ props.item.delegation_id !== null ? "assignment" : "fa-minus" }}</v-icon>
                </span>
              </template>
              <span>
                {{ props.item.delegation_number === null ? $t("global.notSet") : props.item.delegation_number }}
              </span>
            </v-tooltip>
          </td>
          <td>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <v-icon size="24">{{ props.item.delegation_order_id !== null ? "send" : "fa-minus" }}</v-icon>
                </span>
              </template>
              <span>
                {{ props.item.delegation_order_number === null ? $t("global.notSet") : props.item.delegation_order_number }}
              </span>
            </v-tooltip>
          </td>
          <td>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <v-icon size="24">
                    <template v-if="props.item.status === 1">fa-check-circle</template>
                    <template v-else-if="props.item.status === -1">fa-times-circle</template>
                    <template v-else-if="props.item.status === 2">fa-wallet</template>
                    <template v-else>fa-question-circle</template>
                  </v-icon>
                </span>
              </template>
              <span>
                <template v-if="props.item.status === 1">{{ $t("delegation.advance.status.approved") }}</template>
                <template v-else-if="props.item.status === -1">{{ $t("delegation.advance.status.rejected") }}</template>
                <template v-else-if="props.item.status === 2">{{ $t("delegation.advance.status.paid") }}</template>
                <template v-else>{{ $t("delegation.advance.status.unknown") }}</template>
              </span>
            </v-tooltip>
          </td>
          <td>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <v-icon>{{ props.item.payment_type === 1 ? "fa-money-bill" : "fa-university" }}</v-icon>
                </span>
              </template>
              <span>
                {{ props.item.payment_type === 1 ? $t("delegation.advance.paymentCash") : $t("delegation.advance.paymentTransfer") }}
              </span>
            </v-tooltip>
          </td>
          <td>{{ props.item.payment_date }}</td>
          <td class="text-no-wrap text-center">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <v-btn icon @click="print(props.item.id)">
                    <v-icon>mdi-file-download</v-icon>
                  </v-btn>
                </span>
              </template>
              <span>
                {{ $t("delegation.advance.download") }}
              </span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <v-btn icon @click="downloadAttachments(props.item.id)">
                    <v-icon>fa-download</v-icon>
                  </v-btn>
                </span>
              </template>
              <span>
                {{ $t("delegation.advance.downloadAttachments") }}
              </span>
            </v-tooltip>
            <v-tooltip bottom v-if="props.item.can_accept">
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <v-btn icon @click="accept(props.item)">
                    <v-icon>check_circle</v-icon>
                  </v-btn>
                </span>
              </template>
              <span>
                {{ $t("delegation.advance.accept") }}
              </span>
            </v-tooltip>
            <v-tooltip bottom v-if="props.item.can_accept">
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <v-btn icon @click="reject(props.item)">
                    <v-icon>block</v-icon>
                  </v-btn>
                </span>
              </template>
              <span>
                {{ $t("delegation.advance.reject") }}
              </span>
            </v-tooltip>
            <v-tooltip bottom v-if="props.item.can_payout">
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <v-btn icon @click="openPayoutDialog(props.item)">
                    <v-icon>fa-cash-register</v-icon>
                  </v-btn>
                </span>
              </template>
              <span>
                {{ $t("delegationAdvance.payoutTooltip") }}
              </span>
            </v-tooltip>
          </td>
        </tr>
      </template>
    </v-data-table>
    <v-dialog v-model="payoutDialog" max-width="300px">
      <v-card>
        <v-card-text>
          <v-form ref="form">
            <h3>Wypłać zaliczkę</h3>
            <date-picker :model.sync="payoutAdvanceDate" label="Data wypłacenia" :rules="[v => !!v || $t('global.required')]" />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="blue darken-1" @click="payoutDialog = false" text>Anuluj</v-btn>
          <v-btn color="blue darken-1" text @click="payout()" :disabled="$store.getters['Ajax/loading']">Wypłać</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import DelegationMenu from "../../components/DelegationMenu";
import fileDownload from "../../repository/downloadFile";
import vue from "../../main";
import DatePicker from "../../components/CustomInputComponents/DatePicker";
import IconDataTableHeader from "../../components/IconDataTableHeader";

export default {
  name: "DelegationAdvanceView",
  components: { IconDataTableHeader, DatePicker, DelegationMenu },
  data: () => ({
    delegationAdvances: [],
    search: null,
    payoutDialog: false,
    payoutAdvanceId: null,
    payoutAdvanceDate: null
  }),
  computed: {
    headers() {
      return [
        { text: this.$t("delegation.advance.headers.userName"), value: "user_full_name" },
        { text: this.$t("delegation.advance.headers.amount"), value: "amount" },
        { text: this.$t("delegation.advance.headers.assignedDelegationSettlement"), value: "delegation_number", icon: "assignment" },
        { text: this.$t("delegation.advance.headers.assignedDelegationOrder"), value: "delegation_order_number", icon: "send" },
        { text: "Status", value: "status", icon: "fa-question-circle" },
        { text: this.$t("delegation.advance.headers.paymentType"), value: "payment_type", icon: "fa-cash-register" },
        { text: this.$t("delegation.advance.headers.paymentDate"), value: "payment_date" },
        { text: this.$t("global.actions"), sortable: false, value: "actions", align: "center" }
      ];
    }
  },
  methods: {
    accept(item) {
      let result = this.$store.dispatch("confirmDialog", {
        content: vue.$t("delegationAdvance.acceptDialog.content"),
        title: vue.$t("delegationAdvance.acceptDialog.title")
      });
      result.then(() => {
        this.axios({
          url: "delegation-advance/accept",
          data: { delegation_advance_id: item.id },
          method: "POST"
        })
          .then(resp => {
            if (resp.data.status === "OK") {
              this.download();
            } else if (resp.data.status === "error") {
              //TODO WYSWIETLENIE DYMKA BLEDU WALIDACJI
            }
          })
          .catch(() => {
            //TODO obsluga bledow
          });
      });
    },
    reject(item) {
      let result = this.$store.dispatch("confirmDialog", {
        content: vue.$t("delegationAdvance.rejectDialog.content"),
        title: vue.$t("delegationAdvance.rejectDialog.title")
      });
      result.then(() => {
        this.axios({
          url: "delegation-advance/reject",
          data: { delegation_advance_id: item.id },
          method: "POST"
        })
          .then(resp => {
            if (resp.data.status === "OK") {
              this.download();
            } else if (resp.data.status === "error") {
              //TODO WYSWIETLENIE DYMKA BLEDU WALIDACJI
            }
          })
          .catch(() => {
            //TODO obsluga bledow
          });
      });
    },
    payout() {
      if (this.$refs.form.validate()) {
        this.axios
          .post("delegation-advance/payout", {
            delegation_advance_id: this.payoutAdvanceId,
            payout_date: this.payoutAdvanceDate
          })
          .then(() => {
            this.$store.dispatch("snackbarPush", {
              text: this.$t("global.saved")
            });
            this.download();
          });
        this.payoutDialog = false;
      }
    },
    openPayoutDialog(item) {
      this.payoutDialog = true;
      this.payoutAdvanceId = item.id;
      this.payoutAdvanceDate = null;
    },
    download() {
      this.axios.get("delegation-advance/list").then(resp => {
        this.delegationAdvances = resp.data;
      });
    },
    print(id) {
      fileDownload("delegation-advance/print?delegation_advance_id=" + id);
    },
    downloadAttachments(id) {
      fileDownload("delegation-advance/download?delegation_advance_id=" + id);
    }
  },
  created() {
    this.download();
  }
};
</script>

<style scoped></style>
